import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, RefreshCw, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Logo } from "@/components/Logo";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";

// Typical webhook arrival is < 10s; we treat 30s as the upper bound for the
// estimate before showing "taking longer than usual".
const ESTIMATED_SECONDS = 30;

/**
 * Post-checkout landing page. Polls the subscription row (populated by the
 * Paddle webhook) and routes the user into the dashboard once it lands.
 * Falls back to a "Continue anyway" link after ~20s in case the webhook is slow.
 */
export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { isAuthenticated, role, loading: authLoading } = useAuth();
  const { subscription, refresh } = useSubscription();
  const [elapsed, setElapsed] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Poll every 2s for the webhook to insert the row
  useEffect(() => {
    if (subscription) return;
    const id = setInterval(() => {
      refresh();
      setElapsed((s) => s + 2);
    }, 2000);
    return () => clearInterval(id);
  }, [subscription, refresh]);

  // Auto-redirect once subscription is detected
  useEffect(() => {
    if (!subscription || authLoading) return;
    const dest = role === "manager" || role === "admin" ? "/dashboard" : "/rep";
    const t = setTimeout(() => navigate(dest, { replace: true }), 1200);
    return () => clearTimeout(t);
  }, [subscription, role, authLoading, navigate]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <p className="text-sm">Please sign in to see your subscription.</p>
            <Link to="/login"><Button>Sign in</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const confirmed = !!subscription;
  const slow = !confirmed && elapsed >= ESTIMATED_SECONDS;
  const remaining = Math.max(0, ESTIMATED_SECONDS - elapsed);
  const progressPct = Math.min(100, (elapsed / ESTIMATED_SECONDS) * 100);

  const manualRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      // Small delay so the spinner is visible even on instant responses
      setTimeout(() => setRefreshing(false), 400);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border/50 px-6 py-4">
        <Link to="/"><Logo variant="full" className="h-12 sm:h-14 object-contain" /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-5">
            {confirmed ? (
              <>
                <div className="w-14 h-14 rounded-full bg-primary/15 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <h1 className="font-display text-2xl font-bold mb-1">You're in.</h1>
                  <p className="text-sm text-muted-foreground">
                    Subscription confirmed. Taking you to your dashboard…
                  </p>
                </div>
                <Link to={role === "manager" || role === "admin" ? "/dashboard" : "/rep"}>
                  <Button className="w-full">
                    Continue <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-primary/10 mx-auto flex items-center justify-center">
                  <Loader2 className="h-7 w-7 text-primary animate-spin" />
                </div>
                <div>
                  <h1 className="font-display text-2xl font-bold mb-1">
                    Subscription is still processing
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Payment received. We're waiting for Paddle to confirm and unlock your access.
                  </p>
                </div>

                {/* Estimated time + progress */}
                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {slow
                        ? "Taking longer than usual"
                        : remaining > 0
                          ? `Estimated ~${remaining}s remaining`
                          : "Almost done…"}
                    </span>
                    <span className="font-mono text-muted-foreground/80">{elapsed}s</span>
                  </div>
                  <Progress value={slow ? 100 : progressPct} className="h-1.5" />
                  <p className="text-[11px] text-muted-foreground">
                    Most subscriptions activate within 10 seconds. We're checking automatically every 2s.
                  </p>
                </div>

                <div className="flex flex-col gap-2 pt-1">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={manualRefresh}
                    disabled={refreshing}
                  >
                    {refreshing
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <RefreshCw className="h-4 w-4 mr-2" />}
                    Refresh access
                  </Button>
                  {slow && (
                    <Link to="/dashboard">
                      <Button variant="ghost" size="sm" className="w-full">
                        Continue anyway
                      </Button>
                    </Link>
                  )}
                </div>

                {slow && (
                  <p className="text-[11px] text-muted-foreground">
                    If this persists, your subscription is safe — check{" "}
                    <Link to="/settings" className="underline">Settings</Link> in a minute.
                  </p>
                )}
              </>
            )}
            <p className="text-[11px] text-muted-foreground pt-2 border-t border-border/50">
              Receipt sent by Paddle.com (Merchant of Record).
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}